'use client'

import { Checkbox } from '@material-tailwind/react'
import Button from '@/components/ui/Button';
import { AddressElement, CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import { useAccountUpgradeMutation, useAccountUpgradeWithAchMutation, useValidateCouponLazyQuery } from 'graphql/generated/graphql'
import React, { ChangeEvent, Fragment, useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import * as Yup from 'yup'
import { useFormDataContext } from '@/hook/useFormData';
import { CouponDetailType, FinConnectSession } from 'types/common';
import Link from 'next/link';
import { NextThreeMonths } from '@/lib/utils';
import debounce from 'lodash.debounce';
import { useDispatch } from 'react-redux';
import { setUser } from 'features/authSlice';

const validationSchema = Yup.object({
    paymentType: Yup.string()
        .oneOf(["card", "ach"], "Invalid payment type")
        .required("Payment type is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    terms: Yup.boolean().oneOf([true], "You must accept the terms").required(),
});

interface StripeCheckoutFormProps {
    totalAmount: number
    checkoutType: string
    setTaxExemption: (val: boolean) => void
    setFinConnSession: (val: FinConnectSession) => void
    setPaymentState: any
    planId: string
    priceId: string
    finConnSession?: FinConnectSession
    setCouponDetail: (val: CouponDetailType) => void
}

const cardElementOptions = {
  showIcon: true,
  placeholder: 'Card Number',
  name: 'cardNumber',
  style: {
    base: {
      fontSize: "16px",
      color: "#333",
      "::placeholder": { color: "#9CA3AF" },
    },
    invalid: {
      color: "#EF4444",
      iconColor: "#EF4444",
    },
  },
};

const StripeCheckoutForm: React.FC<StripeCheckoutFormProps> = ({ 
    totalAmount, 
    checkoutType, 
    priceId,
    planId,
    finConnSession,
    setTaxExemption,
    setFinConnSession,
    setPaymentState,
    setCouponDetail
}) => {
    const stripe = useStripe()
    const elements = useElements()
    const dispatch = useDispatch()

    const { formData, setFormData } = useFormDataContext()
    const [couponIsValidMsg, setCouponIsValidMsg] = useState('')

    const [upgrade, { loading }] = useAccountUpgradeMutation({fetchPolicy: 'no-cache'})
    const [achSubscription, {loading: achLoading}] = useAccountUpgradeWithAchMutation({fetchPolicy: 'network-only'})
    const [validateCoupon] = useValidateCouponLazyQuery({fetchPolicy: 'no-cache'})

    const debouncedApplyCoupon = useCallback(
        debounce((code: string) => {
        if (code.length >= 6 && code.length <= 15) {
            handleApplyCoupon(code);
        } else setCouponDetail(null)
        }, 500),
        []
    );

    useEffect(() => {
    return () => {
        debouncedApplyCoupon.cancel();
    };
    }, [debouncedApplyCoupon, dispatch]);

    const handleCardChange = (event, setFieldValue) => {
        setFieldValue(event.elementType, event.complete);
    };

    const resetAddress = (sameAddress: boolean) => {
        const addressElement = elements.getElement(AddressElement);
        if(!addressElement) return;

        if(sameAddress) {
            addressElement.update({
                defaultValues: {
                    name: `${formData.firstName} ${formData.lastName}`,
                    phone: formData.contactPhone,
                    address: {
                        country: 'US',
                        city: formData.city,
                        line1: formData.streetAddress,
                        state: formData.state,
                        postal_code: formData.zip,
                    }
                }
            })
        }
        else {
          addressElement.clear();
        }
    };

    const handleApplyCoupon = async (code: string) => {
        try {
            const res = (await validateCoupon({
                variables: { code }
            })).data;

            if (res?.validateCoupon?.valid) {
                const newCoupon = {
                    discount: res?.validateCoupon?.discount,
                    duration_in_months: res?.validateCoupon?.duration_in_months
                };

                setCouponDetail(newCoupon);
            } else {
                setCouponDetail(null);
            }
        } catch (error) {
            console.error(error);
            setCouponIsValidMsg(error?.[0]?.message)
        }
    };

    const handleCouponChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        debouncedApplyCoupon(inputValue);
    };

    return (
        <Formik
            initialValues={{
                paymentType: checkoutType,
                email: formData.contactEmail || '',
                coupon: '',
                terms: true,
                cardNumber: false, 
                cardExpiry: false, 
                cardCvc: false,
                exemptTax: false,
                account_number: '',
                routing_number: '',
                account_holder_type: '',
                allow_for_billing: true          
            }}
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={async(values, { resetForm, setSubmitting }) => {
                setPaymentState((prev) => ({ ...prev, success: false, failed: false}))
                try {
                    if (!stripe || !elements) return;

                    const cardElement = elements.getElement(CardNumberElement);

                    // Get address details
                    const addressElement = elements.getElement(AddressElement);
                    const { complete, value } = await addressElement.getValue();

                    if (!complete) {
                        toast.error("Please fill in the address details.");
                        return;
                    }

                    const billing_details = {
                        name: value.name,
                        email: values.email,
                        phone: value.phone,
                        address: {
                            line1: value.address.line1,
                            line2: value.address.line2,
                            city: value.address.city,
                            state: value.address.state,
                            postal_code: value.address.postal_code,
                            country: value.address.country,
                        }
                    }

                    // Create a PaymentMethod
                    if(checkoutType === 'card') {
                        const { error, paymentMethod } = await stripe.createPaymentMethod({
                            type: 'card',
                            card: cardElement,
                            billing_details: {
                                ...billing_details 
                            },
                        });
    
                        if (error) {
                            console.error(error);
                            toast.error(error.message);
                            setSubmitting(false);
                            return;
                        }

                        // Process subscription
                        const { data } = await upgrade({
                            variables: {
                                paymentMethodID: paymentMethod.id,
                                input: {
                                    email: paymentMethod.billing_details.email,
                                    full_name: paymentMethod.billing_details.name,
                                    tax_exemption: values.exemptTax,
                                    plan_id: planId,
                                    price_id: priceId,
                                    amount: totalAmount * 100,
                                    payment_type: 'card',
                                    coupon_code: values.coupon,
                                    billing_details: {
                                        addressLine1: value.address.line1,
                                        addressLine2: value.address.line2,
                                        city: value.address.city,
                                        state: value.address.state,
                                        postalCode: value.address.postal_code,
                                        country: value.address.country,
                                    }
                                }
                            }
                        })
                        if (data.upgradeToProfessional.success) {
                            const updateUserDetails = {
                                subscription_status: data.upgradeToProfessional.status,
                                stripe_customer_id: data.upgradeToProfessional.customerID,
                                current_period_end: new Date(data.upgradeToProfessional.currentPeriodEnd * 1000).toISOString()
                            }
                            dispatch(setUser(updateUserDetails))
                            // setFormData((prevData) => ({
                            //     ...prevData,
                            //     stripe_customer_id: data.upgradeToProfessional.customerID,
                            //     stripe_sub_id: data.upgradeToProfessional.id,
                            //     subscription_status: data.upgradeToProfessional.status,
                            //     subscription_expiry: data.upgradeToProfessional.currentPeriodEnd,
                            //     payment_type: checkoutType
                            // }))
                            setPaymentState((prevState: any) => ({
                                ...prevState,
                                success: true
                            }))
                        } else {
                            setPaymentState((prevState: any) => ({
                                ...prevState,
                                failed: true
                            }))
                        }

                        setSubmitting(false);

                    } 

                    if(checkoutType === 'ach') {
                        const sessionResult = await stripe.collectFinancialConnectionsAccounts({
                            clientSecret: finConnSession?.clientSecret
                        });
                    
                        // Check if an error occurred
                        if (sessionResult.error) {
                            console.error("Error linking bank account:", sessionResult.error);
                            toast.error("Session secret expired")
                            return;
                        }

                        if(sessionResult.financialConnectionsSession) {
                            try {
                                const achRes = (await achSubscription({
                                    variables: {
                                        customerID: finConnSession.customerID,
                                        bankAccountID: sessionResult.financialConnectionsSession.accounts[0].id,
                                        input: {
                                            email: formData.contactEmail,
                                            full_name: value.name,
                                            tax_exemption: values.exemptTax,
                                            plan_id: planId,
                                            price_id: priceId,
                                            amount: totalAmount * 100,
                                            payment_type: 'ach',
                                            coupon_code: values.coupon,
                                            billing_details: {
                                                addressLine1: value.address.line1,
                                                addressLine2: value.address.line2,
                                                city: value.address.city,
                                                state: value.address.state,
                                                postalCode: value.address.postal_code,
                                                country: value.address.country,
                                            }
                                        }
                                    }
                                })).data
                                if(achRes.createACHSubscription) {
                                    setFinConnSession(undefined)
                                    const updateUserDetails = {
                                        subscription_status: achRes.createACHSubscription.status,
                                        stripe_customer_id: achRes.createACHSubscription.customerID,
                                        current_period_end: new Date(achRes.createACHSubscription.currentPeriodEnd * 1000).toISOString()
                                    }
                                    dispatch(setUser(updateUserDetails))
                                    setPaymentState((prevState: any) => ({
                                        ...prevState,
                                        success: true
                                    }))
                                }
                            } catch (error) {
                                setPaymentState((prevState: any) => ({
                                    ...prevState,
                                    failed: true
                                }))
                            }
                        }
                    }

                } catch (error) {
                    console.error('Payment processing error:', error)
                    setPaymentState((prev: any) => ({ ...prev, failed: true }))
                }
            }}
        >
        {({ values, setFieldValue, handleChange, handleSubmit, isSubmitting, isValid, dirty }) => (
            <Form onSubmit={handleSubmit}>
                <div className="flex mt-4 justify-between items-center">
                    <h3 className=" font-medium">Billing info</h3>
                    <div className="flex items-center">
                    <Checkbox 
                        name='allow_for_billing' 
                        defaultChecked
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            setFieldValue('allow_for_billing', e.target.checked)
                            resetAddress(e.target.checked)
                        }}
                        className="w-5 h-5 checked:outline-none checked:ring-2 checked:bg-primary checked:ring-[#6EAC36]" 
                    />
                    <label className="ml-2 text-sm">Billing is same as account address</label>
                    </div>
                </div>
                <div className="flex flex-1 flex-col gap-2 mb-3">
                    <AddressElement 
                        options={{
                            mode: 'billing',
                            allowedCountries: ["US"],
                            fields: {
                                phone: 'always'
                            },
                            validation: {
                                phone: {
                                    required: 'always'
                                }
                            },
                            defaultValues: {
                                name: `${formData.firstName} ${formData.lastName}` || '',
                                phone: formData.contactPhone || '',
                                address: {
                                    country: 'US',
                                    city: formData.city || '',
                                    line1: formData.streetAddress || '',
                                    state: formData.state || '',
                                    postal_code: formData.zip || '',
                                }
                            },
                            autocomplete: {
                                mode: "google_maps_api",
                                apiKey: process.env.NEXT_PUBLIC_GOOGLE_MAP_KEY!
                            }
                        }} 
                        onChange={async(event) => {
                            if (event.complete) {
                                setFormData((prevData) => ({
                                    ...prevData,
                                    state: event.value.address.state,
                                    city: event.value.address.city,
                                    streetAddress: event.value.address.line1 || event.value.address.line2,
                                    zip: event.value.address.postal_code
                                }))
                                // try {
                                //     const res  = (await getTaxDetails({
                                //         variables: {
                                //             priceID: formData?.stripe_price_id,
                                //             input: {
                                //                 address_source: event.value.address.line1 || event.value.address.line2,
                                //                 country: 'US',
                                //                 state: event.value.address.state,
                                //                 currency: 'usd',
                                //                 postal_code: event.value.address.postal_code,
                                //                 amount: totalAmount,
                                //             }
                                //         }
                                //     })).data
                                //     if(res.computeTax) {
                                //         const { __typename, ...rest } = res?.computeTax;
                                //         console.log("tax", rest)
                                //         setTaxDetails(rest)
                                //     }
                                // } catch (error) {
                                //     console.log(error)
                                // }
                            }
                        }}
                        // onChange={(event) => {
                        //     const {
                        //         address: { line1, line2, city, state, postal_code, country },
                        //         phone,
                        //     } = event.value;
                        //     setFormData((prevData) => ({
                        //         ...prevData,
                        //         streetAddress: line1,
                        //         contactPhone: phone,
                        //         city,
                        //         state,
                        //         zip: postal_code,
                        //     }))
                        // }}
                        className='py-1 w-full' 
                    />
                </div>
                {/* <div className='flex gap-3 justify-between w-full mb-3'>
                    <div className="flex flex-1 flex-col gap-2">
                        <label className='text-sm text-gray-600'>Country</label>
                        <Field
                            type="text"
                            name="country"
                            placeholder="United States"
                            defaultValue={values.country}
                            className="-mt-1 h-12 w-full rounded-lg border-[#CBD7DD] placeholder:text-base focus:border-primary focus:ring-primary"
                        />
                        <ErrorMessage
                            name="city"
                            component="div"
                            className="text-sm text-red-500"
                        />
                    </div>
                    <div className="flex flex-1 flex-col gap-2">
                        <label className='text-sm text-gray-600'>City</label>
                        <Field
                            type="text"
                            name="city"
                            placeholder="Los Angelos"
                            defaultValue={values.city}
                            className="-mt-1 h-12 w-full rounded-lg border-[#CBD7DD] placeholder:text-base focus:border-primary focus:ring-primary"
                        />
                        <ErrorMessage
                            name="city"
                            component="div"
                            className="text-sm text-red-500"
                        />
                    </div>
                    <div className="flex flex-1 flex-col gap-2">
                        <label className='text-sm text-gray-600'>Zip code</label>
                        <Field
                            type="number"
                            name="zip"
                            placeholder="12345"
                            defaultValue={values.zip}
                            className="-mt-1 h-12 w-full rounded-lg border-[#CBD7DD] placeholder:text-base focus:border-primary focus:ring-primary"
                        />
                        <ErrorMessage
                            name="zip"
                            component="div"
                            className="text-sm text-red-500"
                        />
                    </div>
                </div> */}
                <div className="flex flex-1 flex-col gap-2 mb-3">
                    <label className='text-sm text-gray-600'>Billing Email</label>
                    <Field
                        type="email"
                        name="email"
                        placeholder="Enter email address"
                        className="-mt-1 h-12 w-full rounded-lg border-[#CBD7DD]/40 placeholder:text-base focus:border-primary focus:ring-primary"
                    />
                    <ErrorMessage
                        name="email"
                        component="div"
                        className="text-sm text-red-500"
                    />
                </div>
                <div className="mt-1">
                    <div className="flex justify-between items-center">
                        {/* <h3 className="font-medium">{checkoutType === 'card' ? 'Credit/Debit Card info' : 'Bank Account info'}</h3> */}
                        <h3 className="font-medium">{checkoutType === 'card' ? 'Credit/Debit Card info' : ''}</h3>
                        <div className="flex flex-col items-center">
                            <div className='flex gap-2 items-center'>
                                <Checkbox 
                                    name='exemptTax'
                                    className="w-5 h-5 checked:outline-none checked:ring-2 checked:bg-primary checked:ring-[#6EAC36]" 
                                    onChange={(e) => {
                                        setTaxExemption(e.target.checked)
                                        setFieldValue('exemptTax', e.target.checked)
                                        setFormData((prevData) => ({
                                            ...prevData,
                                            tax_exemption: e.target.checked
                                        }))
                                    }}
                                />
                                <label className="text-sm">Tax exempt business</label>
                            </div>
                            {formData.tax_exemption && (
                                <a 
                                    target='__blank'
                                    href="https://mail.google.com/mail/?view=cm&fs=1&to=hello@culineerapp.com&su=Tax%20Exemption%20Certificate"
                                    className='text-sm text-primary cursor-pointer pointer-events-auto'
                                >
                                    Click to Submit Tax Exempt Certificate
                                </a>
                            )}
                        </div>
                    </div>
                    <div className="mt-1 space-y-3">
                        {/* <div className="flex flex-1 flex-col gap-2 mb-3">
                            <label className='text-sm text-gray-600'>{checkoutType === 'card' ? 'Card' : 'Bank'} Holder Name</label>
                            <Field
                            type="text"
                            name="holder_name"
                            placeholder="Enter name"
                            defaultValue={values.holder_name}
                            className="-mt-1 h-12 w-full rounded-lg border-[#CBD7DD] placeholder:text-base focus:border-primary focus:ring-primary"
                            />
                            <ErrorMessage
                            name="holder_name"
                            component="div"
                            className="text-sm text-red-500"
                            />
                        </div> */}
                        {checkoutType === 'card' ? (
                            <Fragment>
                                <div className="relative">
                                    <div className="flex flex-1 flex-col gap-2 mb-3">
                                        <label className='text-sm text-gray-600'>Card Number</label>
                                        <div className="border border-[#CBD7DD]/40 rounded-lg p-3 focus-within:border-2 focus-within:border-primary placeholder:text-base focus:border-primary focus:ring-primary">
                                            <CardNumberElement  
                                                className='py-0.5 w-full'
                                                options={cardElementOptions}
                                                onChange={(event) => handleCardChange(event, setFieldValue)}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="flex gap-3">
                                    <div className="flex flex-1 flex-col gap-2 mb-3">
                                        <label className='text-sm text-gray-600'>Expiry date</label>
                                        <div className="border border-[#CBD7DD]/40 rounded-lg p-3 focus-within:border-2 focus-within:border-primary placeholder:text-base focus:border-primary focus:ring-primary">
                                            <CardExpiryElement 
                                                className='py-0.5 w-full'
                                                onChange={(event) => handleCardChange(event, setFieldValue)}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex flex-1 flex-col gap-2 mb-3">
                                        <label className='text-sm text-gray-600'>CVV</label>
                                        <div className="border border-[#CBD7DD]/40 rounded-lg p-3 focus-within:border-2 focus-within:border-primary placeholder:text-base focus:border-primary focus:ring-2 focus:ring-primary">
                                            <CardCvcElement 
                                                className='py-0.5 w-full'
                                                onChange={(event) => handleCardChange(event, setFieldValue)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Fragment>
                        ): (
                            <Fragment>
                                {/* <div className="flex flex-1 flex-col gap-2 mb-3">
                                    <label className='text-sm text-gray-600'>Account Nmuber</label>
                                    <Field
                                        type="text"
                                        name="account_number"
                                        placeholder="XXXXXXXXXX"
                                        className="-mt-1 h-12 w-full rounded-lg border-[#CBD7DD]/40 placeholder:text-base focus:border-primary focus:ring-primary"
                                    />
                                    <ErrorMessage
                                        name="account_number"
                                        component="div"
                                        className="text-sm text-red-500"
                                    />
                                </div>
                                <div className="flex gap-3 mb-3 xs:flex-col xxxs:flex-col xxs:flex-col">
                                    <div className="flex flex-1 flex-col gap-2 mb-3">
                                        <label className='text-sm text-gray-600'>Account Holder Type</label>
                                        <Field
                                            type="text"
                                            name="account_holder_type"
                                            placeholder="e.g individual or company"
                                            className="-mt-1 h-12 w-full rounded-lg border-[#CBD7DD]/40 placeholder:text-base focus:border-primary focus:ring-primary"
                                        />
                                        <ErrorMessage
                                            name="account_holder_type"
                                            component="div"
                                            className="text-sm text-red-500"
                                        />
                                    </div>
                                    <div className="flex flex-1 flex-col gap-2 mb-3">
                                        <label className='text-sm text-gray-600'>Routing Number</label>
                                        <Field
                                            type="text"
                                            name="routing_number"
                                            placeholder="XXXXXXXXXX"
                                            className="-mt-1 h-12 w-full rounded-lg border-[#CBD7DD]/40 placeholder:text-base focus:border-primary focus:ring-primary"
                                        />
                                        <ErrorMessage
                                            name="routing_number"
                                            component="div"
                                            className="text-sm text-red-500"
                                        />
                                    </div>
                                </div> */}
                            </Fragment>
                        )}
                    </div>
                </div>
                {/* Coupon Code */}
                <div className="flex flex-1 flex-col gap-2 my-3">
                    <label className='text-sm text-gray-600'>Coupon Code</label>
                    <Field 
                        name="coupon"
                        maxLength={15}
                    >
                        {({ field, form }) => (
                            <input
                                {...field}
                                onChange={(e) => {
                                    field.onChange(e);
                                    handleCouponChange(e);
                                }}
                                value={field.value}
                                className="-mt-1 h-12 px-3 w-full rounded-lg border border-[#CBD7DD]/50 placeholder:text-base focus:border-primary focus:ring-primary"
                                placeholder="Enter coupon code"
                                maxLength={15}
                            />
                        )}
                    </Field>
                    {couponIsValidMsg && (
                        <div className='py-2 text-red-500'>{couponIsValidMsg}</div>
                    )}
                </div>
        
                {/* Terms and Pay Button */}
                <div className="mt-6">
                    <div className="flex items-center">
                        <Checkbox 
                            defaultChecked
                            name='terms'
                            onChange={(e) => setFieldValue('term', e.target.checked)}
                            className="w-5 h-5 checked:outline-none checked:ring-2 checked:bg-primary checked:ring-[#6EAC36]" 
                        />
                        <label className="ml-2 text-sm">I agree to the <Link href={'/terms-of-service'} target='__blank' className="text-blue-600 underline">Payment Terms & conditions</Link></label>
                    </div>
                    
                    <Button className="flex mt-4 w-full bg-primary text-white py-3 rounded-lg font-medium items-center justify-center" type={'submit'} disable={loading || isSubmitting || (!(values.cardNumber && values.cardExpiry && values.cardCvc) && checkoutType === 'card')}>
                        {loading || achLoading || isSubmitting ? (
                            <FontAwesomeIcon
                                icon={faSpinner}
                                className={'mr-2 h-6 w-6 animate-spin text-white'}
                            />
                        ) : (
                            <span>Pay - ${Intl.NumberFormat('en-US', {minimumFractionDigits: 2}).format(totalAmount || 0)} <span className='text-sm'>{formData?.balance !== 0 && `from ${NextThreeMonths()}`}</span></span>
                        )}
                    </Button>
                </div>
            </Form>
        )}
        </Formik>
    )
}

export default StripeCheckoutForm
                                                                                                                                                                                                                                                                                                                                                       